import type { Room } from 'livekit-client'

import { clientLogInfo, flushClientLogs } from '@/src/lib/clientLog'
import {store} from '@/src/store/rootReducer'

/**
 * Комнаты, для которых мы уже послали запасной `/api/remove_participant`.
 *
 * Повторный запрос на одну и ту же комнату не нужен: либо участник уже снят
 * LiveKit после `room.disconnect()`, либо первый запрос ещё в полёте. WeakSet
 * не держит ссылку на Room после размонтирования и не мешает сборщику мусора.
 */
const removeSentFor = new WeakSet<Room>()

/**
 * Префикс логов ухода из зала ожидания, чтобы в Loki (`app=meet-front`) было
 * видно, что человек ушёл сам, а не был выкинут организатором.
 */
const LOG_PREFIX = 'waiting-room-leave'

/**
 * Параметры запасного удаления участника из комнаты.
 *
 * `roomName` важнее `room.name`: на клиенте имя комнаты иногда ещё пустое,
 * а стор встречи его уже знает. Без имени LiveKit не найдёт участника.
 */
export type RemoveWaitingParticipantParams = {
  room: Room
  livekitUrl: string
  identity: string | undefined
  roomName?: string
}

/**
 * Запасной путь снятия гостя из зала ожидания при уходе со страницы.
 *
 * `room.disconnect()` рвёт WebRTC, но на выгрузке вкладки промис часто не
 * успевает дойти до сервера. Параллельно шлём `fetch` с `keepalive: true`
 * на `/api/remove_participant`. На кнопке «Назад» страница жива — вызывающий
 * может дождаться промиса, чтобы список орга успел обновиться до PreJoin.
 */
export async function removeWaitingParticipant({
  room,
  livekitUrl,
  identity,
  roomName,
}: RemoveWaitingParticipantParams): Promise<void> {
  if (!identity) {
    return
  }

  const resolvedRoomName = roomName || room.name

  if (!resolvedRoomName || !livekitUrl) {
    clientLogInfo(`${LOG_PREFIX} skip remove: missing room or livekitUrl`, {
      room: resolvedRoomName,
      identity,
      hasLivekitUrl: Boolean(livekitUrl),
    })
    flushClientLogs(true)

    return
  }

  if (removeSentFor.has(room)) {
    return
  }

  removeSentFor.add(room)

  const body = {
    room: resolvedRoomName,
    identity,
    token: store.getState().token.accessToken,
  }

  clientLogInfo(`${LOG_PREFIX} remove participant`, {
    room: resolvedRoomName,
    identity,
    hasLivekitUrl: true,
  })
  flushClientLogs(true)

  // keepalive: true критично на pagehide — без него браузер отменяет запрос.
  // Ошибки сети глотаем: запрос не должен ронять встречу.
  try {
    await fetch(
      `${window.location.origin}/api/remove_participant?livekit_url=${encodeURIComponent(livekitUrl)}`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
        keepalive: true,
      },
    )
  } catch {
    return
  }
}
